import { DEBUG, GLOBAL_MESSAGE_EVENT, type PayloadDataType } from './index';
import { useHelperStore } from '~/stores/helpers';
import { useClientStore } from '~/stores/clients';
import { useChannelsStore } from '~/stores/channels';
import { useMemberPostStore } from '~/stores/memberPosts';
import { handleStatusCode } from '../helpers/handleStatusCode';
import type { Emitter } from '~/plugins/emitter';

type UpdateChannelUser = {
  eventType: 'CHANNEL_ACCESS_GRANTED' | 'CHANNEL_ACCESS_REMOVED';
  channelId: number;
  channelUuid: string;
  userId: number;
};

const fetchClientChannels = async (clientId) => {
  const piniaClient = useClientStore();
  await piniaClient.getClientOwnedChannels({
    clientId,
    page: -1,
    pageSize: -1,
  });
};

/**
 * Handles the UPDATE_TEAM pubsub event.
 *
 * @param {PayloadDataType} PayloadDataType - Team data, sends teamId, teamName
 *
 * @returns {Promise<void>}
 */
export async function handleUserChannels(
  ...[_channel, data]: PayloadDataType<UpdateChannelUser>
) {
  if (DEBUG) console.log('handlePusherMessages', data);

  const { subscribeUserToChannel, unsubscribeUserToChannel } = useNuxtApp()
    .$sockets as {
    subscribeUserToChannel: (channelUuid: string) => void;
    unsubscribeUserToChannel: (channelUuid: string) => void;
  };
  const emitter = useNuxtApp().$emitter as Emitter;
  const route = useRoute();
  const clientId = route.params.clientId;
  const piniaChannels = useChannelsStore();
  const piniaPosts = useMemberPostStore();
  const piniaHelper = useHelperStore();

  const { status } = piniaHelper.latestResponseStatus as { status: number };

  // Determine action based on event type
  const isGranted = data.eventType === 'CHANNEL_ACCESS_GRANTED';
  const isRemoved = data.eventType === 'CHANNEL_ACCESS_REMOVED';
  const channelUuid = data.channelUuid as string;
  // const userRemoved =
  //   piniaUser.getUserGetter.summary.basicUserInfo.userId === data.userId &&
  //   isRemoved;
  // const removedFromStudy = piniaChannels.channels.find(
  //   (c) => c.channelId === data.channelId
  // );
  // const addedToStudy = piniaClient.channelsOwnedByClients.items.find(
  //   (c) => c.clientChannelId === data.channelId
  // );

  // const removedFromChannelMessage = `You have been removed from the study: ${removedFromStudy?.name}.`;
  // const removedFromChannelMessage = 'User has unsubscribed from the study';
  // const addedToChannelMessage = `You have been added to the study: ${addedToStudy?.name}.`;
  // const addedToChannelMessage =
  //   'User has been successfully subscribed to the study';

  if (channelUuid) {
    if (isGranted) {
      subscribeUserToChannel(channelUuid);
    } else if (isRemoved) {
      unsubscribeUserToChannel(channelUuid);
      piniaPosts.removePostsFromCollection(String(data.channelId));
      piniaChannels.removeChannel(data.channelId);
      const currentUserStudy = Number(route.params.channelId);
      if (currentUserStudy === data.channelId) {
        navigateTo('/');
      }
    }
  }

  const message = isGranted
    ? 'User has been successfully subscribed to the study'
    : 'User has unsubscribed from the study';

  // Emit the message event
  emitter.$emit(GLOBAL_MESSAGE_EVENT, {
    status,
    eventType: data.eventType,
    message,
    deviceWidth: window?.innerWidth || 0,
  });

  const statusData = handleStatusCode(status);

  if (statusData.isSuccess) {
    const channelId = data.channelId;
    if (isGranted) {
      await piniaChannels.getChannelSummary({
        countryId: '',
        channelId,
        availableRolesCategory: '',
      });
      const key = channelId;
      const updatedChannel = piniaChannels.channelSummary[key];
      if (updatedChannel) {
        piniaChannels.setActiveChannel(updatedChannel);
      }
    }
    if (clientId) {
      await fetchClientChannels(clientId);
    }
  }
}
